<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { api } from '@/api'
import { useAudioPlayerStore } from '@/stores'
import { useI18n } from '@/composables/useI18n'
import { AutoRenewalNotice, PaymentInlinePanel } from '@/composables/payment'
import { CHAPTER_LOCK } from '@/constants'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

const audioStore = useAudioPlayerStore()
const router = useRouter()
const { t } = useI18n()

const skeleton = ref(true)
const initialized = ref(false)
const initializedContentId = ref('')
const book = ref<GetContentGetcontentdetailResponse['data']>()
const showChapterList = ref(false)
const showSpeedOptions = ref(false)
const showPaymentPanel = ref(false)
// 版位商品构成(自动续费提示用),由 PaymentInlinePanel 上报
const productComposition = ref({ hasSubscribe: false, hasTopup: false })
const selectedChapterId = ref('')
const selectedChapterName = ref('')
const unlockLoading = ref(false)
const progressBarRef = ref<HTMLElement>()
const isProgressDragging = ref(false)
const draggingProgressTime = ref(0)
const speedOptions = [0.75, 1, 1.5, 2]

const progressPercent = computed(() => {
  if (!audioStore.duration)
    return 0
  const currentTime = isProgressDragging.value ? draggingProgressTime.value : audioStore.currentTime
  return (currentTime / audioStore.duration) * 100
})

const currentTimeText = computed(() => formatTime(isProgressDragging.value ? draggingProgressTime.value : audioStore.currentTime))
const durationText = computed(() => formatTime(audioStore.duration))

function formatTime(seconds: number): string {
  if (!seconds || Number.isNaN(seconds))
    return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

function getProgressTimeByClientX(clientX: number) {
  if (!progressBarRef.value || !audioStore.duration)
    return null

  const rect = progressBarRef.value.getBoundingClientRect()
  const percent = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
  return percent * audioStore.duration
}

function previewProgressByClientX(clientX: number) {
  const newTime = getProgressTimeByClientX(clientX)
  if (newTime === null)
    return

  draggingProgressTime.value = newTime
}

function onProgressPointerMove(event: PointerEvent) {
  if (!isProgressDragging.value)
    return
  event.preventDefault()
  previewProgressByClientX(event.clientX)
}

function stopProgressDrag() {
  if (!isProgressDragging.value)
    return

  isProgressDragging.value = false
  window.removeEventListener('pointermove', onProgressPointerMove)
  window.removeEventListener('pointerup', onProgressPointerUp)
  window.removeEventListener('pointercancel', stopProgressDrag)
}

function onProgressPointerUp() {
  const targetTime = draggingProgressTime.value
  stopProgressDrag()
  audioStore.seek(targetTime)
}

function onProgressPointerDown(event: PointerEvent) {
  event.preventDefault()
  isProgressDragging.value = true
  draggingProgressTime.value = audioStore.currentTime
  previewProgressByClientX(event.clientX)
  window.addEventListener('pointermove', onProgressPointerMove, { passive: false })
  window.addEventListener('pointerup', onProgressPointerUp)
  window.addEventListener('pointercancel', stopProgressDrag)
}

function onSpeedSelect(rate: number) {
  audioStore.setPlaybackRate(rate)
  showSpeedOptions.value = false
}

function onClose() {
  audioStore.minimizePlayer()
}

function onRead() {
  const chapterId = audioStore.currentChapterId
  if (!chapterId)
    return

  const progress = audioStore.duration > 0
    ? Math.round((audioStore.currentTime / audioStore.duration) * 100)
    : 0

  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: audioStore.contentId, chapterId },
    query: {
      audioProgress: String(progress),
      skipPosition: '1',
    },
  })

  // 用户主动跳转阅读页，清除听书侧的支付中状态，避免阅读页支付返回后误拉起听书播放器
  audioStore.paymentRequiredChapterId = ''
  audioStore.clearPaymentChapterId()
  audioStore.clearLsPaymentChapterId()
  audioStore.saveState()
  audioStore.minimizePlayer()
}

function toggleChapterList() {
  showChapterList.value = !showChapterList.value
  showSpeedOptions.value = false
}

function toggleSpeedOptions() {
  showSpeedOptions.value = !showSpeedOptions.value
  showChapterList.value = false
}

async function onChapterSelect(chapterId: string) {
  const chapter = audioStore.chapterList.find(ch => ch.id === chapterId)
  if (!chapter)
    return

  showChapterList.value = false

  if (chapter.lockStatus !== CHAPTER_LOCK.LOCK && chapter.ttsAudioUrl) {
    audioStore.selectChapter(chapterId)
    return
  }

  unlockLoading.value = true
  try {
    const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
      contentId: audioStore.contentId,
      contentChapterId: chapterId,
      sequenceNo: 1,
    })
    chapter.lockStatus = CHAPTER_LOCK.UNLOCK
    chapter.ttsAudioUrl = data.ttsAudioUrl
    chapter.bookChapterFileUrl = data.bookChapterFileUrl
    audioStore.selectChapter(chapterId)
  }
  catch (error: any) {
    const { code } = error
    if (code === 403) {
      audioStore.audioElement?.pause()
      audioStore.isPlaying = false
      audioStore.audioLoading = false
      selectedChapterId.value = chapterId
      selectedChapterName.value = chapter.chapterName || ''
      showPaymentPanel.value = true
      audioStore.paymentRequiredChapterId = chapterId
      audioStore.saveLsPaymentChapterId(chapterId)
      audioStore.savePaymentChapterId(chapterId)
      audioStore.saveState()
    }
  }
  finally {
    unlockLoading.value = false
  }
}

async function handlePaySuccess() {
  // 支付完成后页面会刷新/重定向，解锁和续播交给回跳后的 App.vue restoreState 流程
  // 这里只关掉支付面板，不做解锁操作（避免 PayTemplateSuccess 在跳转支付网关前误触发）
  showPaymentPanel.value = false
  unlockLoading.value = false
}

function onClosePaymentPanel() {
  showPaymentPanel.value = false
  selectedChapterId.value = ''
  selectedChapterName.value = ''
  audioStore.paymentRequiredChapterId = ''
  audioStore.clearPaymentChapterId()
  audioStore.clearLsPaymentChapterId()
  audioStore.saveState()
}

// 监听自动播放触发的付费请求
watch(() => audioStore.paymentRequiredChapterId, (chapterId) => {
  if (chapterId) {
    const chapter = audioStore.chapterList.find(ch => ch.id === chapterId)
    selectedChapterId.value = chapterId
    selectedChapterName.value = chapter?.chapterName || ''
    showPaymentPanel.value = true
    audioStore.visible = true
    audioStore.miniMode = false
  }
})

async function fetchBook() {
  if (!audioStore.contentId)
    return false
  const contentId = audioStore.contentId
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>(
    '/content/getContentDetail',
    { params: { contentId } },
  )
  if (contentId !== audioStore.contentId)
    return false
  book.value = data
  if (data) {
    audioStore.serialStatus = data.finishStatus
    audioStore.saveState()
  }
  return true
}

async function initPlayer() {
  const contentId = audioStore.contentId
  if (!contentId)
    return
  skeleton.value = true
  try {
    const loaded = await fetchBook()
    if (loaded) {
      initialized.value = true
      initializedContentId.value = contentId
    }
  }
  catch {}
  finally {
    skeleton.value = false
  }
}

function onVisibleChange(val: boolean) {
  if (!val)
    return
  showChapterList.value = false
  showSpeedOptions.value = false
  const contentChanged = initializedContentId.value !== audioStore.contentId
  if (contentChanged) {
    initialized.value = false
    book.value = undefined
    if (!audioStore.paymentRequiredChapterId) {
      showPaymentPanel.value = false
      selectedChapterId.value = ''
      selectedChapterName.value = ''
    }
  }
  if (audioStore.restored) {
    skeleton.value = false
    audioStore.restored = false
    // 恢复音频播放器（从 sessionStorage 恢复时 audioElement 为 null）
    audioStore.initAudio()
    const chapter = audioStore.currentChapter
    if (chapter?.ttsAudioUrl && audioStore.audioElement) {
      audioStore.audioElement.src = chapter.ttsAudioUrl
      audioStore.audioElement.currentTime = audioStore.currentTime
      audioStore.audioElement.playbackRate = audioStore.playbackRate
      audioStore.audioLoading = false
    }
    if (contentChanged)
      initPlayer()
  }
  else if (!initialized.value || contentChanged) {
    initPlayer()
  }
}

watch(() => audioStore.visible, onVisibleChange)

onMounted(() => {
  if (audioStore.visible)
    onVisibleChange(audioStore.visible)
})

onBeforeUnmount(() => {
  stopProgressDrag()
})
</script>

<template>
  <Transition name="audio-popup">
    <div
      v-if="audioStore.visible"
      class="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-#151A22"
    >
      <img
        v-if="audioStore.contentCoverUrl"
        :src="audioStore.contentCoverUrl"
        class="absolute inset-0 w-full h-full object-cover scale-110 blur-24px opacity-65"
        alt=""
      >
      <div class="absolute inset-0 bg-gradient-to-b from-[#0E0538]/75 to-[#071335]" />

      <div v-if="skeleton" class="flex-1 flex items-center justify-center">
        <BasicSpinner />
      </div>

      <template v-else>
        <div class="relative z-1 flex items-center justify-between px-16px py-12px">
          <div class="p-2 cursor-pointer" @click="onClose">
            <i-icon-park-outline-down class="text-20px text-white" />
          </div>
          <div class="flex-1 text-center">
            <p class="text-14px text-white/90 font-medium">
              {{ audioStore.currentChapter?.chapterName || '' }}
            </p>
          </div>
          <div class="w-36px" />
        </div>

        <div class="relative z-1 flex-1 flex flex-col items-center justify-center px-24px">
          <div class="relative mb-24px">
            <img
              :src="audioStore.contentCoverUrl"
              class="w-200px h-280px rounded-16px shadow-2xl object-cover"
              alt="Book Cover"
            >
          </div>
          <h2 class="text-18px font-bold text-white text-center mb-8px line-clamp-2">
            {{ audioStore.contentName }}
          </h2>
          <p class="text-13px text-white/50">
            {{ audioStore.currentChapter?.chapterName || '' }}
          </p>
        </div>

        <div class="relative z-1 px-24px pb-16px">
          <div
            ref="progressBarRef"
            class="relative h-4px bg-white/20 rounded-full cursor-pointer mb-8px touch-none"
            @pointerdown="onProgressPointerDown"
          >
            <div
              class="absolute left-0 top-0 h-full rounded-full"
              style="background: var(--primary-color, #9875FB)"
              :style="{ width: `${progressPercent}%` }"
            />
            <div
              class="absolute top-1/2 -translate-y-1/2 w-12px h-12px rounded-full bg-white shadow-md"
              :style="{ left: `calc(${progressPercent}% - 6px)` }"
            />
          </div>
          <div class="flex justify-between text-11px text-white/50 mb-20px">
            <span>{{ currentTimeText }}</span>
            <span>{{ durationText }}</span>
          </div>

          <div class="flex items-center justify-center gap-36px mb-24px">
            <button
              class="w-30px h-30px flex items-center justify-center text-[#DCD4FF] disabled:opacity-30"
              :disabled="!audioStore.hasPrevChapter"
              @click="audioStore.playPrevChapter"
            >
              <img src="/images/audio/prev-chapter.png" class="w-23px h-23px" alt="">
            </button>
            <button class="w-32px h-32px flex items-center justify-center text-[#DCD4FF]" @click="audioStore.skip(-15)">
              <img src="/images/audio/rewind15.png" class="w-25px h-25px" alt="">
            </button>
            <button
              class="w-56px h-56px rounded-full flex items-center justify-center text-white"
              style="background: var(--primary-color, #9875FB)"
              @click="audioStore.togglePlay"
            >
              <img
                :src="audioStore.audioLoading ? '/images/audio/loading-mini.png' : audioStore.isPlaying ? '/images/audio/pause-small.png' : '/images/audio/play-small.png'"
                class="w-28px h-28px"
                :class="{ 'loading-spin': audioStore.audioLoading }"
                alt=""
              >
            </button>
            <button class="w-32px h-32px flex items-center justify-center text-[#DCD4FF]" @click="audioStore.skip(15)">
              <img src="/images/audio/forward15.png" class="w-25px h-25px" alt="">
            </button>
            <button
              class="w-30px h-30px flex items-center justify-center text-[#DCD4FF] disabled:opacity-30"
              :disabled="!audioStore.hasNextChapter"
              @click="audioStore.playNextChapter"
            >
              <img src="/images/audio/next-chapter.png" class="w-23px h-23px" alt="">
            </button>
          </div>

          <div class="grid grid-cols-[56px_1fr_56px] items-center gap-24px px-8px pb-16px">
            <button class="w-56px flex flex-col items-center gap-4px text-white/70" @click="toggleChapterList">
              <img src="/images/audio/chapter-list.png" class="w-24px h-24px" alt="">
              <span class="text-10px">{{ t('Chapters') }}</span>
            </button>
            <button
              class="w-full h-40px rounded-full flex items-center justify-center gap-6px text-14px text-white/90"
              style="background: rgba(255,255,255,0.15)"
              @click="onRead"
            >
              <i-icon-park-outline-book-open class="text-16px" />
              <span>{{ t('Read') }}</span>
            </button>
            <button class="w-56px flex flex-col items-center gap-4px text-white/70" @click="toggleSpeedOptions">
              <img src="/images/audio/speed.png" class="w-24px h-24px" alt="">
              <span class="w-full text-center text-10px">{{ audioStore.playbackRate }}x</span>
            </button>
          </div>
        </div>

        <div
          v-if="showSpeedOptions"
          class="absolute bottom-0 left-0 right-0 z-20 bg-white rounded-t-16px max-h-50dvh overflow-hidden flex flex-col"
        >
          <div class="flex items-center justify-between px-16px py-12px border-b border-gray-100">
            <span class="text-14px text-gray-900 font-bold">{{ t('Speed') }}</span>
            <button class="p-2 text-gray-400" @click="showSpeedOptions = false">
              <i-icon-park-outline-close class="text-16px" />
            </button>
          </div>
          <div class="overflow-y-auto flex-1 px-16px py-8px">
            <button
              v-for="rate in speedOptions"
              :key="rate"
              class="w-full flex items-center justify-between h-57px lh-57px border-b border-gray-100 cursor-pointer"
              @click="onSpeedSelect(rate)"
            >
              <span
                class="text-13px"
                :class="audioStore.playbackRate === rate ? 'text-[var(--primary-color,#9875FB)] font-bold' : 'text-gray-700'"
              >{{ rate }}x</span>
              <i-icon-park-outline-check
                v-if="audioStore.playbackRate === rate"
                class="text-14px text-[var(--primary-color,#9875FB)]"
              />
            </button>
          </div>
        </div>

        <div
          v-if="showChapterList"
          class="absolute bottom-0 left-0 right-0 z-20 bg-white rounded-t-16px max-h-50dvh overflow-hidden flex flex-col"
        >
          <div class="flex items-center justify-between px-16px py-12px border-b border-gray-100">
            <span class="text-14px text-gray-900 font-bold">{{ t('ChapterList') }}</span>
            <button class="p-2 text-gray-400" @click="showChapterList = false">
              <i-icon-park-outline-close class="text-16px" />
            </button>
          </div>
          <div class="overflow-y-auto flex-1 px-16px py-8px relative">
            <div
              v-for="chapter in audioStore.chapterList"
              :key="chapter.id"
              class="toc-item h-57px lh-57px flex items-center justify-between cursor-pointer"
              :class="{ 'opacity-50 pointer-events-none': unlockLoading }"
              @click="onChapterSelect(chapter.id)"
            >
              <span
                class="flex-1 text-truncate text-13px"
                :class="chapter.id === audioStore.currentChapterId ? 'text-[var(--primary-color,#9875FB)]! font-bold' : 'text-n-text-1'"
              >{{ chapter.chapterName }}</span>
              <div
                v-if="chapter.id === audioStore.currentChapterId && audioStore.isPlaying"
                class="flex items-end gap-2px h-14px"
              >
                <div class="music-bar w-2px bg-[var(--primary-color,#9875FB)] rounded-full" style="animation-delay: 0s;" />
                <div class="music-bar w-2px bg-[var(--primary-color,#9875FB)] rounded-full" style="animation-delay: 0.2s;" />
                <div class="music-bar w-2px bg-[var(--primary-color,#9875FB)] rounded-full" style="animation-delay: 0.4s;" />
              </div>
              <i-assets-icon-lock
                v-else-if="chapter.lockStatus === CHAPTER_LOCK.LOCK"
                class="w-17px toc-lock-icon"
              />
            </div>
            <div
              v-if="unlockLoading"
              class="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm z-10"
            >
              <BasicSpinner class="w-32px h-32px" />
            </div>
          </div>
        </div>

        <div
          v-if="showPaymentPanel"
          class="absolute bottom-0 left-0 right-0 bg-white rounded-t-16px max-h-70dvh overflow-hidden flex flex-col z-50"
        >
          <div class="flex items-center justify-between px-16px py-12px border-b border-gray-100">
            <span class="text-14px text-gray-800 font-bold">{{ t('UnlockChapter') }}</span>
            <button class="p-2 text-gray-400" @click="onClosePaymentPanel">
              <i-icon-park-outline-close class="text-16px" />
            </button>
          </div>
          <div class="flex-1 overflow-y-auto p-16px">
            <PaymentInlinePanel
              :content-id="audioStore.contentId"
              :content-chapter-id="selectedChapterId"
              :book-name="book?.contentName || audioStore.contentName"
              :chapter-name="selectedChapterName"
              :serial-status="book?.finishStatus || audioStore.serialStatus"
              :dark="false"
              :mark-recommend-on-pay="false"
              @success="handlePaySuccess"
              @update:product-composition="productComposition = $event"
            />

            <!-- 自动续费提示(仅自然用户 + 版位含订阅商品时展示) -->
            <AutoRenewalNotice
              class="opacity-60 mt-16px lh-14px text-10px text-n-text-3 text-left"
              :has-subscribe="productComposition.hasSubscribe"
              :has-topup="productComposition.hasTopup"
            />
          </div>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
.audio-popup-enter-active,
.audio-popup-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.audio-popup-enter {
  transform: translateY(100%);
  opacity: 0;
}
.audio-popup-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.toc-item {
  border-bottom: 1px solid #ECEDEF;
}

.toc-lock-icon {
  color: var(--toc-lock-color, rgba(0, 0, 0, 0.3));
}

.loading-spin {
  animation: loading-spin-rotate 2s linear infinite;
}

@keyframes loading-spin-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.music-bar {
  height: 100%;
  animation: music-bar-jump 0.8s ease-in-out infinite;
}

@keyframes music-bar-jump {
  0%, 100% {
    height: 20%;
  }
  50% {
    height: 100%;
  }
}
</style>
